import React, { useState } from 'react';
import styled from 'styled-components';
import ProjectContact from './ProjectContact';
import RecruitContact from './RecruitContact';
import { company, offices } from './renewalData';
import {
  Inner,
  SectionHead,
  SectionNo,
  SectionShell,
  SectionTitle,
  buttonReset,
  token,
} from './renewalStyles';

const ContactLead = styled.p`
  margin: 0 0 80px;
  font-size: clamp(22px, 3vw, 42px);
  line-height: 1.35;
  font-weight: 800;
  letter-spacing: -0.03em;
  max-width: 900px;

  span {
    color: ${token.primary};
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(340px, 100%), 1fr));
  gap: 64px;
  align-items: start;
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 44px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: ${token.primary};
  margin-bottom: 12px;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.6;

  span {
    font-size: 0.8em;
    color: ${token.muted};
  }
`;

const InfoLink = styled.a`
  font-size: 20px;
  font-weight: 600;
  color: ${token.ink};
`;

const InfoHours = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.8;
  color: ${token.muted};
`;

const MapTabs = styled.div`
  display: flex;
  gap: 2px;
  background: ${token.border};
  border: 1px solid ${token.border};
  border-bottom: 0;
`;

const MapTab = styled.button`
  ${buttonReset};
  flex: 1;
  padding: 14px 12px;
  min-height: 44px;
  font-size: 14px;
  font-weight: 700;
  background: ${({ $on }) => ($on ? '#000' : '#fff')};
  color: ${({ $on }) => ($on ? '#fff' : token.muted)};
  transition: background 0.2s, color 0.2s;
`;

const MapFrame = styled.div`
  position: relative;
  border: 1px solid ${token.border};
  height: 320px;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    filter: grayscale(1);
  }
`;

/* 흑백 지도 위에 브랜드 컬러 핀을 얹습니다. 지도 상호작용은 막지 않습니다. */
const MapPin = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, calc(-100% + 9px));
  pointer-events: none;
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.35));
`;

const MapLink = styled.a`
  display: inline-block;
  margin-top: 12px;
  font-size: 13px;
  font-weight: 600;
`;

const FormPanel = styled.div`
  border: 1px solid ${token.border};
  background: ${token.surfaceAlt};
  padding: clamp(28px, 4vw, 48px);
`;

const TabList = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 1px solid ${token.border};
  margin-bottom: 36px;
`;

const Tab = styled.button`
  ${buttonReset};
  border-bottom: 3px solid ${({ $on }) => ($on ? token.primary : 'transparent')};
  padding: 0 20px 16px;
  font-size: 18px;
  font-weight: 700;
  color: ${({ $on }) => ($on ? token.primary : token.faint)};
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: ${token.primary};
  }
`;

/**
 * 폼 탭(tab)과 지도 탭(mapKey)은 헤더의 스크롤 스파이와 완전히 분리되어 있습니다.
 * 예전 시안처럼 activeSection 하나로 겸하면 입사지원 탭을 연 채 스크롤할 때 탭이 되돌아갑니다.
 */
export default function Contact() {
  const [tab, setTab] = useState('inquiry');
  const [mapKey, setMapKey] = useState('head');

  const activeOffice = offices.find(office => office.key === mapKey) || offices[0];

  return (
    <SectionShell id="contact">
      <Inner>
        <SectionHead $gap="24px">
          <SectionNo>05</SectionNo>
          <SectionTitle>CONTACT US</SectionTitle>
        </SectionHead>
        <ContactLead>
          꿈을 현실로 만드는 <span>비알아이티</span>와 함께 하세요.
        </ContactLead>

        <ContactGrid>
          <InfoColumn>
            {offices.map(office => (
              <div key={office.key}>
                <InfoLabel>{office.eyebrow}</InfoLabel>
                <InfoText>
                  {office.lines[0]}
                  <br />
                  {office.lines[1]}
                  {office.note && <span> {office.note}</span>}
                </InfoText>
              </div>
            ))}

            <div>
              <InfoLabel>WEBSITE</InfoLabel>
              <InfoLink href={company.site} target="_blank" rel="noopener noreferrer">
                {company.siteLabel}
              </InfoLink>
            </div>

            <div>
              <InfoLabel>OFFICE HOURS</InfoLabel>
              <InfoHours>
                평일 09:00 – 18:00
                <br />
                주말 및 공휴일 휴무
              </InfoHours>
            </div>

            <div>
              <MapTabs>
                {offices.map(office => (
                  <MapTab
                    key={office.key}
                    type="button"
                    aria-pressed={mapKey === office.key}
                    $on={mapKey === office.key}
                    onClick={() => setMapKey(office.key)}
                  >
                    {office.tabLabel}
                  </MapTab>
                ))}
              </MapTabs>
              <MapFrame>
                <iframe
                  key={activeOffice.key}
                  src={activeOffice.embed}
                  title={activeOffice.mapTitle}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <MapPin aria-hidden="true">
                  <svg width="34" height="46" viewBox="0 0 34 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M17 0C7.6 0 0 7.6 0 17c0 12.2 15.3 27.4 16 28.1.6.6 1.5.6 2 0 .7-.7 16-15.9 16-28.1C34 7.6 26.4 0 17 0z"
                      fill={token.primary}
                    />
                    <circle cx="17" cy="17" r="6.4" fill="#fff" />
                  </svg>
                </MapPin>
              </MapFrame>
              <MapLink href={activeOffice.naver} target="_blank" rel="noopener noreferrer">
                네이버 지도에서 보기 ↗
              </MapLink>
            </div>
          </InfoColumn>

          <FormPanel>
            <TabList role="tablist">
              <Tab
                type="button"
                role="tab"
                aria-selected={tab === 'inquiry'}
                $on={tab === 'inquiry'}
                onClick={() => setTab('inquiry')}
              >
                프로젝트 의뢰
              </Tab>
              <Tab
                type="button"
                role="tab"
                aria-selected={tab === 'recruit'}
                $on={tab === 'recruit'}
                onClick={() => setTab('recruit')}
              >
                입사지원
              </Tab>
            </TabList>

            {tab === 'inquiry' ? <ProjectContact /> : <RecruitContact />}
          </FormPanel>
        </ContactGrid>
      </Inner>
    </SectionShell>
  );
}
